import { buildApiUrl, httpClient } from "shared/api/httpClient"
import {
  extractApiErrorMessage,
  getAuthHeaders,
  invalidateSessionOnAuthError,
} from "shared/api/serviceSupport"

export interface UploadAvatarResponse {
  avatarUrl: string
}

const MAX_AVATAR_BYTES = 2 * 1024 * 1024
const ALLOWED_AVATAR_MIMES = ["image/jpeg", "image/png", "image/webp"] as const

export function validateAvatarFile(file: File): string | null {
  if (!ALLOWED_AVATAR_MIMES.includes(file.type as (typeof ALLOWED_AVATAR_MIMES)[number])) {
    return "Formato inválido. Envie JPG, PNG ou WebP."
  }

  if (file.size > MAX_AVATAR_BYTES) {
    return "Arquivo muito grande. Tamanho máximo: 2MB."
  }

  return null
}

function buildAvatarEndpoint(userId: string) {
  return buildApiUrl(`/api/configuracao/users/${encodeURIComponent(userId)}/avatar`)
}

export function buildAvatarImageUrl(userId: string, cacheKey?: string | null) {
  const base = buildAvatarEndpoint(userId)
  if (!cacheKey) {
    return base
  }

  const separator = base.includes("?") ? "&" : "?"
  return `${base}${separator}v=${encodeURIComponent(cacheKey)}`
}

export async function uploadUserAvatar(
  userId: string,
  file: File,
  signal?: AbortSignal,
): Promise<UploadAvatarResponse> {
  const validationError = validateAvatarFile(file)
  if (validationError) {
    throw new Error(validationError)
  }

  const formData = new FormData()
  formData.append("avatar", file)

  const { Accept, Authorization } = getAuthHeaders()
  const headers: Record<string, string> = { Accept }
  if (Authorization) {
    headers.Authorization = Authorization
  }

  const res = await httpClient({
    url: buildAvatarEndpoint(userId),
    method: "POST",
    headers,
    body: formData,
    signal,
    retryAttempts: 0,
  })

  if (!res.ok) {
    invalidateSessionOnAuthError(res.status)
    const apiError = extractApiErrorMessage(res.bodyText)
    throw new Error(apiError || "Não foi possível atualizar a foto de perfil.")
  }

  const data = JSON.parse(res.bodyText) as Partial<UploadAvatarResponse>
  if (!data.avatarUrl || typeof data.avatarUrl !== "string") {
    throw new Error("Resposta inválida do servidor ao atualizar a foto.")
  }

  return { avatarUrl: data.avatarUrl }
}

export async function deleteUserAvatar(
  userId: string,
  signal?: AbortSignal,
): Promise<void> {
  const res = await httpClient({
    url: buildAvatarEndpoint(userId),
    method: "DELETE",
    headers: getAuthHeaders(),
    signal,
    retryAttempts: 0,
  })

  if (!res.ok) {
    invalidateSessionOnAuthError(res.status)
    const apiError = extractApiErrorMessage(res.bodyText)
    throw new Error(apiError || "Não foi possível remover a foto de perfil.")
  }
}
